import { PromotionType } from '../../graphQL/commons/types'
import {
  IConditionAction,
  IConfig,
  IPromotionShortcut,
  IPromotionUiCatalog,
} from '../../interfaces'
import { GetPromotionsUiDetails, IProduct } from '../../interfaces/catalog'
import { getComboPromotionUiDetails } from './getComboPromotionUiDetails'
import { getPromotionFields } from '../common/getPromotionFields'
import { getComplexPromotionUiDetails } from './getComplexPromotionUiDetails'
import { getDirectPromotionUiDetails } from './getDirectPromotionUiDetails'
import { getVolumePromotionUiDetails } from './getVolumePromotionUiDetails'

export interface ISetPromotionShortcutsArgs {
  product: IProduct
  config?: IConfig
  includeAllPromotions: boolean
}

const comboComplexPromotionsTypes = [PromotionType.COMPLEX, PromotionType.COMBO]
const promotionsWithoutShortcuts = [PromotionType.DIRECT]

const allPromotionsTypes = [
  PromotionType.COMPLEX,
  PromotionType.COMBO,
  PromotionType.DIRECT,
  PromotionType.VOLUME,
]

const getPromotionDetailsArray: {
  condition: (type: PromotionType) => boolean
  transformAction: GetPromotionsUiDetails
}[] = [
  {
    condition: (type) => type === PromotionType.COMPLEX,
    transformAction: getComplexPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.COMBO,
    transformAction: getComboPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.DIRECT,
    transformAction: getDirectPromotionUiDetails,
  },
  {
    condition: (type) => type === PromotionType.VOLUME,
    transformAction: getVolumePromotionUiDetails,
  },
]

interface IPromotionDetailsTransformerArgs
  extends Pick<ISetPromotionShortcutsArgs, 'product' | 'includeAllPromotions'> {
  promotionDetails?: IPromotionUiCatalog
}

const promotionDetailsTransformer: IConditionAction<
  IPromotionDetailsTransformerArgs,
  IPromotionShortcut[]
>[] = [
  {
    condition: ({ promotionDetails }) =>
      Number(promotionDetails?.data?.customerBuys?.groups?.length) > 1 &&
      !!promotionDetails?.data,
    transformAction: ({ promotionDetails, product }) => {
      const { title = '', description, data } = promotionDetails!
      const { customerBuysGroups } = getPromotionFields(data!)

      const result = customerBuysGroups.map<IPromotionShortcut>((group) => {
        const suffix = group.items.reduce<string>(
          (acc, { sku, quantity }, index) => {
            const isFirst = index === 0
            const prefix = isFirst ? '' : ', '
            if (sku === product.sku) {
              return acc.concat(`${prefix}(${quantity}) ${product.name}`)
            } else {
              return acc.concat(`${prefix}(${quantity}) ${sku}`)
            }
          },
          '',
        )

        return {
          id: group.identifier || '',
          title,
          description: `${description} - ${suffix}.`,
          data: {
            promotion: data,
            groupIdentifier: group.identifier,
          },
          showButton: true,
        }
      })
      return result
    },
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      Number(promotionDetails?.data?.customerGets?.groups?.length) > 1,
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: false,
      },
    ],
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      comboComplexPromotionsTypes.some(
        (type) => type === promotionDetails.data?.type,
      ),
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: true,
      },
    ],
  },
  {
    condition: ({ promotionDetails }) =>
      promotionDetails !== undefined &&
      allPromotionsTypes.some((type) => type === promotionDetails.data?.type),
    transformAction: ({ promotionDetails }) => [
      {
        id: promotionDetails?.data?.id || '',
        title: promotionDetails?.title || '',
        description: promotionDetails?.description || '',
        data: { promotion: promotionDetails!.data },
        showButton: false,
      },
    ],
  },
]

export const getPromotionsShortcuts = ({
  product,
  config,
  includeAllPromotions,
}: ISetPromotionShortcutsArgs): IPromotionShortcut[] => {
  if (product.promotions?.length && config) {
    const { promotions } = product

    const promotionsToBeDisplayed = includeAllPromotions
      ? promotions
      : [promotions[0]]

    if (
      !includeAllPromotions &&
      ![...comboComplexPromotionsTypes, ...promotionsWithoutShortcuts].some(
        (type) => type === promotionsToBeDisplayed[0]?.type,
      )
    ) {
      return []
    }

    const promotionsUiDetails = promotionsToBeDisplayed
      .map((promotion) => {
        return getPromotionDetailsArray
          .find(({ condition }) => condition(promotion.type))
          ?.transformAction({
            promotion,
            config,
          })
      })
      .filter((item): item is IPromotionUiCatalog => item !== undefined)

    const promotionsShortcuts = promotionsUiDetails.reduce<
      IPromotionShortcut[]
    >((acc, promotionDetails) => {
      const argsCondition = {
        promotionDetails,
        product,
        includeAllPromotions,
      }
      const validConditionAction = promotionDetailsTransformer.find(
        ({ condition }) => condition(argsCondition),
      )
      if (validConditionAction) {
        return [
          ...acc,
          ...validConditionAction.transformAction({
            promotionDetails,
            product,
            includeAllPromotions,
          }),
        ]
      }
      return acc
    }, [])

    return promotionsShortcuts
  }

  return []
}

export default getPromotionsShortcuts
